import CardHeader from '@mui/material/CardHeader';
import MuiLink from '@mui/material/Link';
import { alpha, emphasize, styled } from '@mui/material/styles';
import { NavItem } from '@qpr/components/Expandable/nav-items-view-model';
import React from 'react';
import { Link } from 'react-router-dom';
import { checkIfExternal } from '../../../utils/url-utils';

const PREFIX = 'MenuHeader';

const classes = {
  disabled: `${PREFIX}-disabled`,
  linkStyle: `${PREFIX}-linkStyle`,
  megaiconStyle: `${PREFIX}-megaiconStyle`,
  cardHeader: `${PREFIX}-cardHeader`,
  avatar: `${PREFIX}-avatar`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.disabled}`]: {
    minHeight: '64px',
    borderRadius: '4px',
    color: alpha(theme.palette.getContrastText(theme.palette.text.primary), 0.38),
    background: emphasize(theme.palette.primary.main, 0.15)
  },

  [`& .${classes.linkStyle}`]: {
    textDecoration: 'none',
    '&:hover': {
      background: theme.palette.primary.dark
    }
  },

  [`& .${classes.megaiconStyle}`]: {
    fontSize: '48px',
    verticalAlign: 'middle',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2)
  },

  [`& .${classes.cardHeader}`]: {
    minHeight: '64px',
    borderRadius: '4px',
    color: 'white',
    background: emphasize(theme.palette.primary.main, 0.15)
  },

  [`& .${classes.avatar}`]: {
    minWidth: theme.spacing(1)
  }
}));

interface MenuHeaderProps {
  item: NavItem;
  tenantSymbol: string;
  setItemDescription: React.Dispatch<React.SetStateAction<string | undefined>>;
  onClose: () => void;
}

const MenuHeader = ({
  item = { _id: 0, title: '', link: '', enabled: false, children: [] },
  tenantSymbol = '',
  setItemDescription = () => {
    return null;
  },
  onClose
}: MenuHeaderProps) => {
  const icon =
    typeof item.icon === 'object'
      ? { ...item.icon, props: { ...item.icon.props, className: classes.megaiconStyle } }
      : ' ';

  const content = (
    <CardHeader
      onMouseEnter={() => setItemDescription((item.enabled && item.description) || '')}
      onMouseLeave={() => setItemDescription('')}
      style={{ padding: '0px', margin: '0px' }}
      avatar={icon}
      title={item.title}
      titleTypographyProps={{ align: 'left', variant: 'h6' }}
      className={
        // eslint-disable-next-line no-nested-ternary
        item.enabled
          ? item.link !== undefined && item.link !== ''
            ? `${classes.cardHeader} ${classes.linkStyle}`
            : classes.cardHeader
          : classes.disabled
      }
      data-testid="megamenu-menu-header"
      classes={{ avatar: classes.avatar }}
    />
  );

  if (item.enabled && item.link !== undefined && item.link !== '') {
    if (checkIfExternal(item.link)) {
      return (
        <Root>
          <MuiLink
            underline="none"
            onClick={onClose}
            target="_blank"
            rel="noopener noreferrer"
            href={item.link}
            data-testid="link-external"
            className={classes.linkStyle}
          >
            {content}
          </MuiLink>
        </Root>
      );
    }
    return (
      <Root>
        <Link
          style={{ textDecorationLine: 'none' }}
          onClick={onClose}
          to={`/${tenantSymbol}${item.link}`}
          data-testid="link-internal"
          className={classes.linkStyle}
        >
          {content}
        </Link>
      </Root>
    );
  }

  return <Root>{content}</Root>;
};

export default MenuHeader;
